import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const PAGE_SIZE_OPTIONS = [25, 50, 100];
export const DEFAULT_PAGE_SIZE = 25;

/**
 * Sprint 11.1 — Shared pagination UX for all list screens.
 *
 * Backend contract (see `app/api/pagination.py`):
 *   request : ?page=1&page_size=25
 *   response: { items, total, page, page_size, total_pages }
 *
 * Props
 * -----
 *  page          : current 1-based page
 *  pageSize      : 25 | 50 | 100
 *  total         : total matching rows across all pages
 *  totalPages    : ceil(total / pageSize)
 *  onPageChange  : (newPage) => void
 *  onPageSizeChange : (newSize) => void
 *  isLoading     : disables controls while server is fetching
 *
 * Layout: "51–75 of 402" on the left, page selector + size selector on the right.
 */
export function DataTablePagination({
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  className = '',
}) {
  const safePage = Math.max(1, Math.min(page, Math.max(1, totalPages)));
  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(total, safePage * pageSize);

  // Build the page-number window. Always show first + last + neighbours of current.
  const pageNumbers = React.useMemo(() => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, safePage - 1);
    const right = Math.min(totalPages - 1, safePage + 1);
    if (left > 2) pages.push('ellipsis-left');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('ellipsis-right');
    pages.push(totalPages);
    return pages;
  }, [totalPages, safePage]);

  if (total === 0) {
    return (
      <div
        className={`flex flex-col gap-3 px-4 py-3 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between ${className}`}
        data-testid="pagination"
      >
        <span data-testid="pagination-range">0 of 0</span>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${className}`}
      data-testid="pagination"
    >
      <p className="text-xs text-muted-foreground tabular-nums" data-testid="pagination-range">
        {start.toLocaleString()}–{end.toLocaleString()} of {total.toLocaleString()}
      </p>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Rows per page</span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
            disabled={isLoading}
          >
            <SelectTrigger className="h-8 w-[72px]" data-testid="pagination-page-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((sz) => (
                <SelectItem key={sz} value={String(sz)} data-testid={`pagination-page-size-${sz}`}>
                  {sz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Pagination className="justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => {
                  e.preventDefault();
                  if (safePage > 1 && !isLoading) onPageChange(safePage - 1);
                }}
                aria-disabled={safePage <= 1 || isLoading}
                className={safePage <= 1 || isLoading ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                data-testid="pagination-prev"
              />
            </PaginationItem>
            {pageNumbers.map((p, idx) =>
              typeof p === 'number' ? (
                <PaginationItem key={p}>
                  <PaginationLink
                    onClick={(e) => {
                      e.preventDefault();
                      if (!isLoading && p !== safePage) onPageChange(p);
                    }}
                    isActive={p === safePage}
                    className={isLoading ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    data-testid={`pagination-page-${p}`}
                  >
                    {p}
                  </PaginationLink>
                </PaginationItem>
              ) : (
                <PaginationItem key={`${p}-${idx}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ),
            )}
            <PaginationItem>
              <PaginationNext
                onClick={(e) => {
                  e.preventDefault();
                  if (safePage < totalPages && !isLoading) onPageChange(safePage + 1);
                }}
                aria-disabled={safePage >= totalPages || isLoading}
                className={safePage >= totalPages || isLoading ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                data-testid="pagination-next"
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
}

/**
 * Hook that mirrors page/pageSize + arbitrary filter values into the URL
 * query string. Browser back/forward navigates between pages naturally,
 * deep links work, and filter state survives a refresh.
 *
 * Usage:
 *   const { params, setPage, setPageSize, setFilter, resetFilters } =
 *     usePagedQueryParams({ initial: { stage: 'all', role: 'all' } });
 *   // params = { page, page_size, stage, role }
 *
 * Notes
 * -----
 *  - Filter values equal to 'all' (or '' / undefined / null) are stripped
 *    from the URL so the URL stays tidy — unless `stripAllFilters` is false
 *    (needed when `all` is a meaningful API value, e.g. Resume Enrichment status).
 *  - Changing any filter resets `page` to 1 automatically.
 */
export function usePagedQueryParams({
  initial = {},
  defaultPageSize = DEFAULT_PAGE_SIZE,
  stripAllFilters = true,
} = {}) {
  const [sp, setSp] = useSearchParams();

  const params = React.useMemo(() => {
    const p = Number(sp.get('page') || 1);
    const ps = Number(sp.get('page_size') || defaultPageSize);
    const filters = { ...initial };
    for (const k of Object.keys(initial)) {
      const v = sp.get(k);
      if (v != null && v !== '') filters[k] = v;
    }
    return {
      page: Number.isFinite(p) && p >= 1 ? p : 1,
      page_size: PAGE_SIZE_OPTIONS.includes(ps) ? ps : defaultPageSize,
      ...filters,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const writeParams = React.useCallback(
    (next) => {
      const usp = new URLSearchParams();
      for (const [k, v] of Object.entries(next)) {
        if (v == null || v === '' || (stripAllFilters && v === 'all')) continue;
        usp.set(k, String(v));
      }
      setSp(usp, { replace: true });
    },
    [setSp, stripAllFilters],
  );

  const setPage = React.useCallback(
    (newPage) => writeParams({ ...params, page: newPage }),
    [params, writeParams],
  );
  const setPageSize = React.useCallback(
    (newSize) => writeParams({ ...params, page: 1, page_size: newSize }),
    [params, writeParams],
  );
  const setFilter = React.useCallback(
    (key, value) => writeParams({ ...params, page: 1, [key]: value }),
    [params, writeParams],
  );
  const setManyFilters = React.useCallback(
    (patch) => writeParams({ ...params, page: 1, ...patch }),
    [params, writeParams],
  );
  const resetFilters = React.useCallback(
    () => writeParams({ page: 1, page_size: params.page_size }),
    [params.page_size, writeParams],
  );

  return { params, setPage, setPageSize, setFilter, setManyFilters, resetFilters };
}
